import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Image } from 'expo-image';
import { getFileIconByExtension, getStatusText, formatFileSize, isImageFile, getStatusColor, getFileTypeColor } from '@/constants/utils/files';
import BasicTableRow from '@/components/Table/BasicTableRow';
import Tooltip from '@/components/Tooltip';

const createStyles = (colors) => StyleSheet.create({
  fileIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  fileImage: {
    width: 28,
    height: 28,
    borderRadius: 8,
  },
  fileName: {
    flexShrink: 1,
    textAlignVertical: 'center',
  },
  fileNameWrapper: {
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
  },
  fileType: {
    textTransform: 'capitalize',
  },
  statusPill: {
    borderRadius: 12,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  statusText: {
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  checkmarkContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.solid_color_primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.text_color_primary,
  },
});

const FileBasicTableRow = ({ 
  file, 
  onPress, 
  isSelected, 
  onSelect,
  selectionDisabled,
  isXS,
  excludeStatus,
  showDelete = false,
  onDelete,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isHovered, setIsHovered] = useState(false);
  
  const fileTypeColor = useMemo(() => getFileTypeColor(file.extension), [file.extension]);
  const statusColor = useMemo(() => getStatusColor(file.processingStatus.status), [file.processingStatus.status]);

  const handleHoverIn = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleHoverOut = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handlePress = useCallback(() => {
    onPress(file);
  }, [onPress, file]);

  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    if (!selectionDisabled || isSelected) {
      onSelect(file);
    }
  }, [selectionDisabled, isSelected, onSelect, file]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(file);
    }
  }, [onDelete, file]);

  const renderFileIcon = useCallback(() => (
    <View style={[styles.fileIcon, { backgroundColor: fileTypeColor + '20' }]}> 
      {isSelected ? (
        <View style={styles.checkmarkContainer}>
          <Icon name="check" size={14} color={colors.text_color_primary} />
        </View>
      ) : (
        isImageFile(file.extension) ? ( 
          <Image
            source={{ uri: file.thumbnailPath }}
            style={styles.fileImage}
            resizeMode="cover"
          />
        ) : (
          <Icon
            name={getFileIconByExtension(file.extension)}
            size={16}
            color={fileTypeColor}
          />
        )
      )}
    </View>
  ), [styles, fileTypeColor, isSelected, file.extension, file.thumbnailPath, colors.text_color_primary]);

  const renderColumn = useCallback((column) => {
    switch (column.id) {
      case 'select':
        return (
          <Tooltip label="Select">
            <Pressable onPress={handleSelect}>
              {renderFileIcon()}
            </Pressable>
          </Tooltip>
        );
      case 'name':
        return (
          <View style={styles.fileNameWrapper}>
            <Text numberOfLines={1} size="n" style={styles.fileName}>
              {file.name}
            </Text>
          </View>
        );
      case 'type':
        return (
          <Text size="s" color="secondary" style={styles.fileType}>
            {file.fileType}
          </Text>
        );
      case 'size':
        return (
          <Text size="s" color="secondary">
            {formatFileSize(file.sizeInBytes)}
          </Text>
        );
      case 'status':
        if (excludeStatus || isXS) return null;
        return (
          <View style={[styles.statusPill, {
            backgroundColor: statusColor.background,
            borderColor: statusColor.text,
          }]}> 
            <Text size="xs" style={[styles.statusText, { color: statusColor.text }]}>
              {getStatusText(file.processingStatus.status)}
            </Text>
          </View>
        );
      case 'delete':
        return (
          <Tooltip label="Delete">
            <Pressable onPress={handleDelete}>
              <Icon name="trash" size={16} color={colors.color_destructive} />
            </Pressable>
          </Tooltip>
        );
      default:
        if (column.render) {
          return column.render(file);
        }
        return null;
    }
  }, [styles, file, statusColor, handleSelect, renderFileIcon, excludeStatus, isXS, showDelete, handleDelete, colors.color_destructive]);

  return (
    <BasicTableRow
      data={file}
      renderColumn={renderColumn}
      onPress={handlePress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      isHovered={isHovered}
    />
  );
};

export default React.memo(FileBasicTableRow); 